/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { CDNs } from '@ckeditor/ckeditor5-metadata';
import { has } from 'lodash';
import { useState } from 'react';

import { useAsyncEffect } from '../useAsyncEffect/useAsyncEffect';

export function useCKEditor5CdnScripts(): [ boolean, unknown ] {
	const [ isCKEditor5Loaded, setIsCKEditor5Loaded ] = useState( false );

	const { error } = useAsyncEffect(
		async () => {
			await loadServices();

			setIsCKEditor5Loaded( true );
		},
		() => Promise.resolve(),
		[]
	);

	return [ isCKEditor5Loaded, error ];
}

async function loadServices() {
	const servicePromises = CDNs.services.map( async ( { resourceUrl, loaderPath } ) => {
		await loadScript( resourceUrl );
		await waitForScriptReady( loaderPath );
	} );

	await Promise.all( servicePromises );
}

async function loadScript( src: string ): Promise<void> {
	if ( document.querySelector( `script[src="${ src }"]` ) ) {
		return Promise.resolve();
	}

	return new Promise( resolve => {
		const scriptTag = document.createElement( 'script' );

		scriptTag.src = src;
		scriptTag.onload = () => resolve();

		document.body.appendChild( scriptTag );
	} );
}

async function waitForScriptReady( loaderPath: string ): Promise<void> {
	return new Promise( resolve => {
		const intervalId = setInterval( () => {
			if ( has( globalThis, loaderPath ) ) {
				clearInterval( intervalId );
				resolve();
			}
		}, 50 );
	} );
}
